/**
 * Editor de un público (`/activa/oficial/base/publicos/nuevo` y `/publicos/:id`), SPEC-BD §6.4.
 *
 * - `?carga=:id` precarga la condición «Carga de origen viene de esa carga» (desde el resultado de una
 *   importación). `?volverA=` es la ruta del asistente de campaña que mandó a crear el público: el
 *   segundo botón vuelve ahí con `&publico=:id`. Sólo se acepta una ruta del asistente (nunca una URL
 *   armada desde afuera: sería una redirección abierta).
 * - El conteo se pide con un debounce de 600 ms cada vez que el filtro queda válido, y una respuesta
 *   vieja se descarta con un contador de secuencia (como `seqVars` del asistente): tres cambios seguidos
 *   son UN pedido, y un pedido lento no pisa el número del filtro nuevo.
 * - Un público fijo (estático) no cambia de tipo ni de filtro: se duplica.
 * - Con cambios sin guardar, cerrar la pestaña pregunta (`beforeunload`), como el editor de plantillas.
 */

import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Copy, Loader2 } from "lucide-react";
import type { ConteoPublico, FiltroPublico, OpcionesBase, PublicoDetalle, TipoPublico } from "../../types.base";
import { baseApi } from "../../baseApi";
import { validarFiltroLocal } from "../../logic/base";
import { CajaError, Esqueleto, R_BASE, llamar, textoFallo } from "../../components/base/comun";
import { ConstructorFiltro } from "../../components/base/ConstructorFiltro";
import { PanelConteo } from "../../components/base/PanelConteo";

const R_CAMPANAS = "/activa/oficial/campanas";
export const DEBOUNCE_CONTEO_MS = 600;

/** `volverA` sólo si es una ruta del asistente de campaña de esta app (sin esquema, sin «//», sin salir de /activa/oficial/campanas). */
export function volverASeguro(v: string | null): string | null {
  if (!v) return null;
  const s = v.trim();
  if (!s.startsWith(`${R_CAMPANAS}/`) || s.includes("//") || s.includes("\\") || /[\s<>"']/.test(s)) return null;
  return s;
}
/** La ruta de vuelta con `publico=:id` (reemplaza uno que ya viniera). */
export function rutaDeVuelta(volverA: string, publicoId: string): string {
  const u = new URL(volverA, "http://local");
  u.searchParams.set("publico", publicoId);
  return `${u.pathname}${u.search}`;
}

const filtroInicial = (carga: string | null): FiltroPublico =>
  carga ? { condiciones: [{ tipo: "carga", op: "alguna", valores: [carga] }] } : { condiciones: [] };

export default function PublicoEditorPage() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const volverA = volverASeguro(sp.get("volverA"));
  const cargaInicial = sp.get("carga");

  const [opciones, setOpciones] = useState<OpcionesBase | null>(null);
  const [publico, setPublico] = useState<PublicoDetalle | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState("");
  const [noEncontrado, setNoEncontrado] = useState(false);
  const [reintento, setReintento] = useState(0);

  const [nombre, setNombre] = useState("");
  const [tipo, setTipo] = useState<TipoPublico>("dinamico");
  const [filtro, setFiltro] = useState<FiltroPublico>(() => filtroInicial(id ? null : cargaInicial));
  const [inicial, setInicial] = useState(() => JSON.stringify({ nombre: "", tipo: "dinamico", filtro: filtroInicial(id ? null : cargaInicial) }));

  const [conteo, setConteo] = useState<ConteoPublico | null>(null);
  const [contando, setContando] = useState(false);
  const [errorConteo, setErrorConteo] = useState("");
  const [firmaContada, setFirmaContada] = useState("");
  const [reintentoConteo, setReintentoConteo] = useState(0);
  const seq = useRef(0);

  const [guardando, setGuardando] = useState(false);
  const [errorNombre, setErrorNombre] = useState("");
  const [errorFiltro, setErrorFiltro] = useState("");
  const [errorGeneral, setErrorGeneral] = useState("");
  const [duplicando, setDuplicando] = useState(false);

  const idNombre = useId();
  const estaticoGuardado = !!publico && publico.tipo === "estatico";

  // ── Carga: opciones de la base y, si es uno existente, el público ────
  useEffect(() => {
    let vivo = true;
    setCargando(true);
    setError("");
    setNoEncontrado(false);
    void (async () => {
      const [lo, lp] = await Promise.all([
        llamar<OpcionesBase>(() => baseApi.opciones()),
        id ? llamar<PublicoDetalle>(() => baseApi.publico(id)) : Promise.resolve(null),
      ]);
      if (!vivo) return;
      setCargando(false);
      if (lp && (!lp.ok || !lp.data)) {
        if (!lp.ok && lp.status === 404) { setNoEncontrado(true); return; }
        setError(lp.ok ? "El servidor no devolvió el público." : textoFallo(lp));
        return;
      }
      if (!lo.ok || !lo.data) { setError(lo.ok ? "El servidor no devolvió las opciones de la base." : textoFallo(lo)); return; }
      setOpciones(lo.data);
      if (lp?.ok && lp.data) {
        const p = lp.data;
        const f: FiltroPublico = { condiciones: Array.isArray(p.filtro?.condiciones) ? p.filtro.condiciones : [] };
        setPublico(p);
        setNombre(p.nombre);
        setTipo(p.tipo);
        setFiltro(f);
        setInicial(JSON.stringify({ nombre: p.nombre, tipo: p.tipo, filtro: f }));
        // El GET ya recalculó el conteo: no se vuelve a pedir para el mismo filtro.
        if (p.conteoActual) { setConteo(p.conteoActual); setFirmaContada(JSON.stringify(f)); }
      }
    })();
    return () => { vivo = false; };
  }, [id, reintento]);

  // ── Conteo en vivo ────────────────────────────────────────────────────
  const catalogo = useMemo(() => (opciones?.campos || []).map((c) => ({ clave: c.clave, etiqueta: c.etiqueta, tipo: c.tipo })), [opciones]);
  const vFiltro = validarFiltroLocal(filtro, catalogo);
  const firmaFiltro = JSON.stringify(filtro);
  const listo = !!opciones && (!id || !!publico);

  useEffect(() => {
    if (!listo || estaticoGuardado) return;
    if (!vFiltro.ok) {
      // Un pedido en vuelo de un filtro que ya no es el de pantalla no se muestra.
      seq.current++;
      setContando(false);
      return;
    }
    if (firmaFiltro === firmaContada) return;
    const aContar = filtro;
    const t = setTimeout(() => {
      const mio = ++seq.current;
      setContando(true);
      setErrorConteo("");
      void (async () => {
        const l = await llamar<ConteoPublico>(() => baseApi.contarPublico(aContar));
        if (mio !== seq.current) return;
        setContando(false);
        if (!l.ok || !l.data) { setErrorConteo(l.ok ? "El servidor no devolvió el conteo." : textoFallo(l)); return; }
        setConteo(l.data);
        setFirmaContada(JSON.stringify(aContar));
      })();
    }, DEBOUNCE_CONTEO_MS);
    return () => clearTimeout(t);
    // `firmaFiltro` resume el filtro; `vFiltro.ok` sale de él.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firmaFiltro, listo, estaticoGuardado, reintentoConteo]);

  const reintentarConteo = () => { setFirmaContada(""); setErrorConteo(""); setReintentoConteo((n) => n + 1); };

  // ── Cambios sin guardar ──────────────────────────────────────────────
  const actual = JSON.stringify({ nombre, tipo, filtro });
  const sucio = listo && actual !== inicial;
  useEffect(() => {
    if (!sucio) return;
    const h = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [sucio]);

  // ── Guardar ──────────────────────────────────────────────────────────
  const guardar = async (despues: "lista" | "campana" | "volver") => {
    if (guardando) return;
    setErrorNombre(""); setErrorFiltro(""); setErrorGeneral("");
    const limpio = nombre.trim();
    if (limpio.length < 1 || limpio.length > 80) { setErrorNombre("Escribí un nombre (hasta 80 caracteres)."); return; }
    if (!estaticoGuardado && !vFiltro.ok) { setErrorFiltro(vFiltro.error); return; }

    let guardadoId = publico?.id || "";
    if (!publico) {
      setGuardando(true);
      const l = await llamar<PublicoDetalle>(() => baseApi.crearPublico({ nombre: limpio, tipo, filtro }));
      setGuardando(false);
      if (!l.ok || !l.data) { mostrarError(l.ok ? null : l); return; }
      guardadoId = l.data.id;
    } else {
      const cambios: { nombre?: string; filtro?: FiltroPublico } = {};
      if (limpio !== publico.nombre) cambios.nombre = limpio;
      if (!estaticoGuardado && firmaFiltro !== JSON.stringify(publico.filtro)) cambios.filtro = filtro;
      if (cambios.nombre !== undefined || cambios.filtro !== undefined) {
        setGuardando(true);
        const l = await llamar<PublicoDetalle>(() => baseApi.editarPublico(publico.id, cambios));
        setGuardando(false);
        if (!l.ok || !l.data) { mostrarError(l.ok ? null : l); return; }
      }
    }
    setInicial(actual);
    const idUrl = encodeURIComponent(guardadoId);
    if (despues === "volver" && volverA) navigate(rutaDeVuelta(volverA, guardadoId));
    else if (despues === "campana") navigate(`${R_CAMPANAS}/nueva?publico=${idUrl}`);
    else navigate(`${R_BASE}/publicos`, { state: { aviso: "Público guardado." } });
  };

  const mostrarError = (l: { status: number; code: string; error: string } | null) => {
    if (!l) { setErrorGeneral("El servidor no devolvió el público."); return; }
    if (l.code === "NOMBRE_PUBLICO_EN_USO") setErrorNombre("Ya hay un público con ese nombre.");
    else if (l.code === "FILTRO_INVALIDO") setErrorFiltro(l.error || "Revisá las condiciones.");
    else setErrorGeneral(textoFallo(l));
  };

  const duplicarComoDinamico = useCallback(async () => {
    if (!publico || duplicando) return;
    setDuplicando(true);
    setErrorGeneral("");
    const l = await llamar<PublicoDetalle>(() => baseApi.duplicarPublico(publico.id, { tipo: "dinamico" }));
    setDuplicando(false);
    if (!l.ok || !l.data) { setErrorGeneral(l.ok ? "El servidor no devolvió la copia." : textoFallo(l)); return; }
    navigate(`${R_BASE}/publicos/${encodeURIComponent(l.data.id)}${volverA ? `?volverA=${encodeURIComponent(volverA)}` : ""}`);
  }, [publico, duplicando, navigate, volverA]);

  // ── Render ───────────────────────────────────────────────────────────
  const volver = (
    <Link to={`${R_BASE}/publicos`} className="text-xs font-semibold text-slate-500 hover:text-slate-700 inline-flex items-center gap-1">
      <ArrowLeft size={13} /> Públicos
    </Link>
  );

  if (noEncontrado) {
    return (
      <div className="space-y-4">
        {volver}
        <div className="rounded-2xl border border-slate-100 bg-white p-8 text-center shadow-sm">
          <p className="text-sm text-slate-600">No encontramos este público (puede que se haya borrado).</p>
          <Link to={`${R_BASE}/publicos`} className="mt-4 h-10 px-4 rounded-xl bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 inline-flex items-center">
            Volver a Públicos
          </Link>
        </div>
      </div>
    );
  }
  if (cargando && !listo) return <div className="space-y-4">{volver}<Esqueleto filas={3} alto="h-28" /></div>;
  if (!listo || !opciones) {
    return (
      <div className="space-y-4">
        {volver}
        <CajaError titulo={id ? "No pudimos traer el público." : "No pudimos traer los datos de la base."} detalle={error} onReintentar={() => setReintento((n) => n + 1)} />
      </div>
    );
  }

  const radio = "mt-0.5 border-slate-300 text-violet-600 focus:ring-violet-500";

  return (
    <div className="space-y-5">
      {volver}
      <h3 className="text-lg font-semibold text-slate-900 tracking-tight">{publico ? publico.nombre : "Nuevo público"}</h3>

      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_20rem] items-start">
        <section className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm space-y-6">
          {errorGeneral && <p className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">{errorGeneral}</p>}

          <div className="max-w-md">
            <label htmlFor={idNombre} className="text-xs font-semibold text-slate-600">Nombre</label>
            <input id={idNombre} value={nombre} maxLength={80} disabled={guardando} onChange={(e) => { setNombre(e.target.value.slice(0, 80)); setErrorNombre(""); }}
              aria-invalid={!!errorNombre || undefined}
              className={`w-full mt-1.5 h-10 rounded-xl border px-3 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 ${errorNombre ? "border-red-300" : "border-slate-200"}`} />
            {errorNombre && <p className="text-xs text-red-600 mt-1" role="alert">{errorNombre}</p>}
          </div>

          <fieldset>
            <legend className="text-xs font-semibold text-slate-600">Tipo</legend>
            <div className="mt-2 space-y-2">
              <label className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer">
                <input type="radio" name="tipo" value="dinamico" checked={tipo === "dinamico"} disabled={!!publico || guardando} onChange={() => setTipo("dinamico")} className={radio} />
                Dinámico: se recalcula cada vez que confirmás una campaña (recomendado)
              </label>
              <label className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer">
                <input type="radio" name="tipo" value="estatico" checked={tipo === "estatico"} disabled={!!publico || guardando} onChange={() => setTipo("estatico")} className={radio} />
                Fijo: guarda la lista de hoy; los contactos que cargues después no entran
              </label>
            </div>
          </fieldset>

          {estaticoGuardado && (
            <div className="flex items-center justify-between gap-3 flex-wrap rounded-xl bg-slate-50 px-4 py-3 text-sm text-slate-700">
              <span>Este público es fijo: para cambiarlo, duplicalo</span>
              <button type="button" onClick={() => void duplicarComoDinamico()} disabled={duplicando}
                className="h-9 px-3 rounded-xl border border-slate-200 bg-white text-sm font-semibold text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1.5 disabled:opacity-50">
                {duplicando ? <Loader2 size={14} className="animate-spin" /> : <Copy size={14} />} Duplicar
              </button>
            </div>
          )}

          <div>
            <h4 className="text-xs font-semibold text-slate-600 mb-2">Condiciones</h4>
            <ConstructorFiltro filtro={filtro} opciones={opciones} onChange={(f) => { setFiltro(f); setErrorFiltro(""); }}
              error={errorFiltro} disabled={estaticoGuardado || guardando} />
          </div>

          <div className="flex gap-2 flex-wrap pt-4 border-t border-slate-100">
            <button type="button" onClick={() => void guardar("lista")} disabled={guardando}
              className="h-10 px-5 rounded-xl bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 inline-flex items-center gap-2 disabled:opacity-50">
              {guardando && <Loader2 size={14} className="animate-spin" />} Guardar
            </button>
            <button type="button" onClick={() => void guardar(volverA ? "volver" : "campana")} disabled={guardando}
              className="h-10 px-5 rounded-xl border border-violet-200 bg-white text-sm font-semibold text-violet-700 hover:bg-violet-50 disabled:opacity-50">
              {volverA ? "Guardar y volver a la campaña" : "Guardar y usar en una campaña"}
            </button>
          </div>
        </section>

        <div className="lg:sticky lg:top-4">
          <PanelConteo
            conteo={conteo}
            contando={contando || (!estaticoGuardado && vFiltro.ok && firmaFiltro !== firmaContada && !errorConteo)}
            error={errorConteo}
            filtroIncompleto={!estaticoGuardado && !vFiltro.ok}
            onReintentar={reintentarConteo}
          />
        </div>
      </div>
    </div>
  );
}
